import { useState, useEffect, useCallback } from 'react'
import { getTopRankedBGGGames, BGGGameDetails } from '@/services/bgg-api'

interface TopGamesHookState {
  games: BGGGameDetails[]
  isLoading: boolean
  error: string | null
}

interface TopGamesHookResult extends TopGamesHookState {
  refreshGames: () => void
}

/**
 * A hook that fetches the top ranked games from BoardGameGeek and provides random selections
 * @param count - Number of random games to select from the top ranked list
 * @param totalPool - Total number of top games to fetch (e.g., top 100)
 * @returns Object containing random games, loading state, error, and refresh function
 */
export function useTopBGGGames(count: number = 5, totalPool: number = 100): TopGamesHookResult {
  const [state, setState] = useState<TopGamesHookState>({
    games: [],
    isLoading: true,
    error: null,
  })

  // Function to get random games from the pool of top games
  const getRandomGames = useCallback((allGames: BGGGameDetails[], num: number) => {
    const shuffled = [...allGames].sort(() => 0.5 - Math.random())
    return shuffled.slice(0, num)
  }, [])

  // Function to fetch top games and select random ones
  const fetchAndSelectGames = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }))

    try {
      const { data, error } = await getTopRankedBGGGames(totalPool)

      if (error) {
        setState(s => ({ ...s, isLoading: false, error }))
        return
      }

      if (!data || data.length === 0) {
        setState(s => ({ ...s, games: [], isLoading: false }))
        return
      }

      // Select random games from the top ranked list
      const randomSelection = getRandomGames(data, count)

      setState(s => ({ ...s, games: randomSelection, isLoading: false }))
    } catch (error) {
      setState(s => ({
        ...s,
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred fetching top games',
      }))
    }
  }, [count, totalPool, getRandomGames])

  // Initial fetch on mount
  useEffect(() => {
    fetchAndSelectGames()
  }, [fetchAndSelectGames])

  // Function to manually refresh the games selection
  const refreshGames = useCallback(() => {
    fetchAndSelectGames()
  }, [fetchAndSelectGames])

  return {
    ...state,
    refreshGames,
  }
}

export default useTopBGGGames
